import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register as registerUser, checkUsername, checkEmail } from '../services/auth';
import './Register.css';

function Register() {
	const [formData, setFormData] = useState({
		username: '',
		email: '',
		password: '',
		password2: ''
	});
	
	const [usernameCheck, setUsernameCheck] = useState({ checked: false, available: false, message: '' });
	const [emailCheck, setEmailCheck] = useState({ checked: false, available: false, message: '' });
	const [passwordMatch, setPasswordMatch] = useState({ checked: false, match: false, message: '' });
	const [agreePrivacy, setAgreePrivacy] = useState(false);  // 🔥 추가
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	// 비밀번호 일치 확인
	useEffect(() => {
		if (formData.password && formData.password2) {
			if (formData.password === formData.password2) {
				setPasswordMatch({ checked: true, match: true, message: '✅ 비밀번호가 일치합니다.' });
			} else {
				setPasswordMatch({ checked: true, match: false, message: '❌ 비밀번호가 일치하지 않습니다.' });
			}
		} else {
			setPasswordMatch({ checked: false, match: false, message: '' });
		}
	}, [formData.password, formData.password2]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value
		});
		
		if (name === 'username') {
			setUsernameCheck({ checked: false, available: false, message: '' });
		}
		if (name === 'email') {
			setEmailCheck({ checked: false, available: false, message: '' });
		}
	};

	const handleUsernameCheck = async () => {
		if (!formData.username) {
			setUsernameCheck({ checked: true, available: false, message: '❌ 아이디를 입력해주세요.' });
			return;
		}

		if (formData.username.length < 4) {
			setUsernameCheck({ checked: true, available: false, message: '❌ 아이디는 4자 이상이어야 합니다.' });
			return;
		}

		try {
			const response = await checkUsername(formData.username);
			setUsernameCheck({
				checked: true,
				available: response.available,
				message: response.available ? '✅ 사용 가능한 아이디입니다.' : '❌ 이미 사용 중인 아이디입니다.'
			});
		} catch (error) {
			setUsernameCheck({
				checked: true,
				available: false,
				message: '❌ 중복 확인에 실패했습니다.'
			});
		}
	};

	const handleEmailCheck = async () => {
		if (!formData.email) {
			setEmailCheck({ checked: true, available: false, message: '❌ 이메일을 입력해주세요.' });
			return;
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(formData.email)) {
			setEmailCheck({ checked: true, available: false, message: '❌ 올바른 이메일 형식이 아닙니다.' });
			return;
		}

		try {
			const response = await checkEmail(formData.email);
			setEmailCheck({
				checked: true,
				available: response.available,
				message: response.available ? '✅ 사용 가능한 이메일입니다.' : '❌ 이미 사용 중인 이메일입니다.'
			});
		} catch (error) {
			setEmailCheck({
				checked: true,
				available: false,
				message: '❌ 중복 확인에 실패했습니다.'
			});
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// 🔥 추가
		if (!agreePrivacy) {
			alert('개인정보처리방침에 동의해주세요.');
			return;
		}

		if (!usernameCheck.available) {
			alert('아이디 중복 확인을 해주세요.');
			return;
		}

		if (!emailCheck.available) {
			alert('이메일 중복 확인을 해주세요.');
			return;
		}

		if (formData.password !== formData.password2) {
			alert('비밀번호가 일치하지 않습니다.');
			return;
		}

		if (formData.password.length < 8) {
			alert('비밀번호는 8자 이상이어야 합니다.');
			return;
		}

		const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
		if (!passwordRegex.test(formData.password)) {
			alert('비밀번호는 영문과 숫자를 포함해야 합니다.');
			return;
		}

		try {
			setLoading(true);
			
			console.log('📝 회원가입 요청:', formData);
			
			await registerUser({
				username: formData.username,
				email: formData.email,
				password: formData.password,
				password2: formData.password2
			});
			
			alert('회원가입이 완료되었습니다!\n로그인 후 이메일 인증을 진행해주세요.');
			navigate('/login');
			
		} catch (error) {
			console.error('❌ 회원가입 실패:', error);
			
			if (error.response?.data) {
				const errorData = error.response.data;
				let errorMessage = '회원가입에 실패했습니다.\n\n';
				
				if (errorData.username) {
					errorMessage += `아이디: ${errorData.username}\n`;
				}
				if (errorData.email) {
					errorMessage += `이메일: ${errorData.email}\n`;
				}
				if (errorData.password) {
					errorMessage += `비밀번호: ${errorData.password}\n`;
				}
				if (errorData.error) {
					errorMessage += errorData.error;
				}
				
				alert(errorMessage);
			} else {
				alert('회원가입에 실패했습니다. 다시 시도해주세요.');
			}
		} finally {
			setLoading(false);
		}
	};

	const getUsernameMessage = () => {
		if (usernameCheck.checked) {
			return usernameCheck.message;
		}
		if (formData.username) {
			return '⚠️ 중복 확인을 해주세요.';
		}
		return '';
	};

	const getEmailMessage = () => {
		if (emailCheck.checked) {
			return emailCheck.message;
		}
		if (formData.email) {
			return '⚠️ 중복 확인을 해주세요.';
		}
		return '';
	};

	return (
		<div className="register-container">
			<div className="register-box">
				<h2>회원가입</h2>
				
				<form onSubmit={handleSubmit}>
					<div className="form-group">
						<label>
							아이디
							{getUsernameMessage() && (
								<span className={`inline-message ${
									usernameCheck.available ? 'success' : 
									usernameCheck.checked ? 'error' : 'warning'
								}`}>
									{getUsernameMessage()}
								</span>
							)}
						</label>
						<div className="input-with-button">
							<input
								type="text"
								name="username"
								value={formData.username}
								onChange={handleChange}
								placeholder="아이디 (4자 이상)"
								required
								autoComplete="username"
							/>
							<button type="button" onClick={handleUsernameCheck} className="btn-check">
								중복확인
							</button>
						</div>
					</div>

					<div className="form-group">
						<label>
							이메일
							{getEmailMessage() && (
								<span className={`inline-message ${
									emailCheck.available ? 'success' : 
									emailCheck.checked ? 'error' : 'warning'
								}`}>
									{getEmailMessage()}
								</span>
							)}
						</label>
						<div className="input-with-button">
							<input
								type="email"
								name="email"
								value={formData.email}
								onChange={handleChange}
								placeholder="이메일 주소"
								required
								autoComplete="email"
							/>
							<button 
								type="button" 
								onClick={handleEmailCheck} 
								className="btn-check"
							>
								중복확인
							</button>
						</div>
						<p className="help-text">
							ℹ️ 로그인 후 이메일 인증을 진행해주세요.
						</p>
					</div>

					<div className="form-group">
						<label>비밀번호</label>
						<input
							type="password"
							name="password"
							value={formData.password}
							onChange={handleChange}
							placeholder="비밀번호 (8자 이상, 영문+숫자)"
							required
							autoComplete="new-password"
						/>
					</div>

					<div className="form-group">
						<label>
							비밀번호 확인
							{passwordMatch.checked && passwordMatch.message && (
								<span className={`inline-message ${passwordMatch.match ? 'success' : 'error'}`}>
									{passwordMatch.message}
								</span>
							)}
						</label>
						<input
							type="password"
							name="password2"
							value={formData.password2}
							onChange={handleChange}
							placeholder="비밀번호 확인"
							required
							autoComplete="new-password"
						/>
					</div>

					{/* 🔥 추가 */}
					<div className="form-group checkbox-group">
						<label className="checkbox-label">
							<input
								type="checkbox"
								checked={agreePrivacy}
								onChange={(e) => setAgreePrivacy(e.target.checked)}
								required
							/>
							<span>
								<Link to="/privacy" target="_blank" className="privacy-link">
									개인정보처리방침
								</Link>에 동의합니다 (필수)
							</span>
						</label>
					</div>

					<button type="submit" className="btn-submit" disabled={loading}>
						{loading ? '처리 중...' : '회원가입'}
					</button>
				</form>

				<div className="login-link">
					이미 계정이 있으신가요? <Link to="/login">로그인</Link>
				</div>
			</div>
		</div>
	);
}

export default Register;
